/**
 * Cart Upsell Component
 *
 * A Web Component for displaying add-on upsell products in the cart.
 * Supports dynamic product assignment, visibility toggling, and cart integration.
 */

class CartUpsell extends HTMLElement {
  constructor() {
    super();

    // State management
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.showIfInCart = this.dataset.showIfInCart === 'true';
    this.isLoading = false;
    this.isVisible = !this.classList.contains('cart-upsell--hidden');

    // DOM references
    this.addButton = null;
    this.dismissButton = null;
    this.variantSelect = null;
    this.errorContainer = null;
    this.successContainer = null;
    this.loadingSpinner = null;
    this.buttonText = null;

    // Bind methods
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.checkVisibilityConditions = this.checkVisibilityConditions.bind(this);
  }

  connectedCallback() {
    this.initializeDOM();
    this.attachEventListeners();
    this.checkVisibilityConditions();

    // Subscribe to cart update events
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  /**
   * Initialize DOM element references
   */
  initializeDOM() {
    this.addButton = this.querySelector('.cart-upsell__add-button');
    this.dismissButton = this.querySelector('.cart-upsell__dismiss');
    this.variantSelect = this.querySelector('.cart-upsell__variant-select');
    this.errorContainer = this.querySelector('.cart-upsell__error');
    this.successContainer = this.querySelector('.cart-upsell__success');
    this.loadingSpinner = this.querySelector('.cart-upsell__loading');
    this.buttonText = this.querySelector('.cart-upsell__button-text');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    if (this.addButton) {
      this.addButton.addEventListener('click', this.handleAddToCart);
    }

    if (this.dismissButton) {
      this.dismissButton.addEventListener('click', this.handleDismiss);
    }

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.handleVariantChange);
    }
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    if (this.addButton) {
      this.addButton.removeEventListener('click', this.handleAddToCart);
    }

    if (this.dismissButton) {
      this.dismissButton.removeEventListener('click', this.handleDismiss);
    }

    if (this.variantSelect) {
      this.variantSelect.removeEventListener('change', this.handleVariantChange);
    }
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange(event) {
    const selectedVariantId = event.target.value;
    this.variantId = selectedVariantId;

    if (this.addButton) {
      this.addButton.dataset.variantId = selectedVariantId;
    }

    // Clear any previous errors
    this.hideError();
  }

  /**
   * Handle add to cart button click
   */
  async handleAddToCart(event) {
    event.preventDefault();

    if (this.isLoading) return;

    // Check if product is already in cart
    if (!this.showIfInCart && this.isProductInCart()) {
      this.showError('This item is already in your cart');
      return;
    }

    this.setLoadingState(true);
    this.hideError();
    this.hideSuccess();

    const variantId = this.variantSelect ? this.variantSelect.value : this.variantId;

    try {
      const response = await this.addToCart(variantId);

      if (response.ok) {
        const data = await response.json();
        this.handleAddSuccess(data);
      } else {
        const error = await response.json();
        this.handleAddError(error);
      }
    } catch (error) {
      this.handleAddError({ message: 'An error occurred. Please try again.' });
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Add item to cart via Shopify API
   */
  async addToCart(variantId) {
    const formData = {
      items: [{
        id: variantId,
        quantity: 1
      }]
    };

    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
  }

  /**
   * Handle successful add to cart
   */
  handleAddSuccess(data) {
    this.showSuccess();

    // Publish cart update event
    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cart: data });
    }

    // Refresh cart drawer
    this.refreshCartDrawer();

    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      this.hideSuccess();

      // Hide upsell if configured to do so
      if (!this.showIfInCart) {
        this.hide();
      }
    }, 3000);
  }

  /**
   * Handle add to cart error
   */
  handleAddError(error) {
    const errorMessage = error.message || error.description || 'Unable to add item to cart';
    this.showError(errorMessage);
  }

  /**
   * Refresh cart drawer to show updated cart
   */
  async refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');

    if (!cartDrawer) return;

    try {
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const newDrawerContent = html.querySelector('.drawer__inner');

      if (newDrawerContent) {
        const currentDrawerContent = cartDrawer.querySelector('.drawer__inner');
        if (currentDrawerContent) {
          currentDrawerContent.innerHTML = newDrawerContent.innerHTML;
        }
      }

      // Update cart icon bubble
      const response2 = await fetch(`${window.Shopify.routes.root}?section_id=cart-icon-bubble`);
      const text2 = await response2.text();
      const html2 = new DOMParser().parseFromString(text2, 'text/html');
      const newCartBubble = html2.querySelector('#cart-icon-bubble');

      if (newCartBubble) {
        const currentCartBubble = document.querySelector('#cart-icon-bubble');
        if (currentCartBubble) {
          currentCartBubble.innerHTML = newCartBubble.innerHTML;
        }
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  /**
   * Handle dismiss button click
   */
  handleDismiss(event) {
    event.preventDefault();
    this.hide();

    // Store dismissal in sessionStorage to prevent showing again this session
    sessionStorage.setItem(`cart-upsell-dismissed-${this.productId}`, 'true');
  }

  /**
   * Handle cart update events
   */
  handleCartUpdate(event) {
    this.checkVisibilityConditions();
  }

  /**
   * Check if visibility conditions are met
   */
  checkVisibilityConditions() {
    // Check if dismissed this session
    const isDismissed = sessionStorage.getItem(`cart-upsell-dismissed-${this.productId}`) === 'true';
    if (isDismissed) {
      this.hide();
      return;
    }

    // Check if product is already in cart
    if (!this.showIfInCart && this.isProductInCart()) {
      this.hide();
      return;
    }

    // Check custom trigger conditions if provided
    const triggerConditions = this.dataset.triggerConditions;
    if (triggerConditions) {
      try {
        const conditions = JSON.parse(triggerConditions);
        if (!this.evaluateConditions(conditions)) {
          this.hide();
          return;
        }
      } catch (error) {
        console.error('Error parsing trigger conditions:', error);
      }
    }

    // All conditions passed, show if not already visible
    if (!this.isVisible) {
      this.show();
    }
  }

  /**
   * Evaluate trigger conditions
   */
  evaluateConditions(conditions) {
    // Example conditions:
    // { minCartValue: 50, requiredProducts: ['123', '456'], excludedProducts: ['789'] }

    const cart = this.getCartData();
    if (!cart) return true; // If we can't get cart data, show by default

    // Check minimum cart value
    if (conditions.minCartValue) {
      const cartTotal = cart.total_price / 100; // Convert cents to dollars
      if (cartTotal < conditions.minCartValue) {
        return false;
      }
    }

    // Check for required products in cart
    if (conditions.requiredProducts && conditions.requiredProducts.length > 0) {
      const hasRequiredProduct = cart.items.some(item =>
        conditions.requiredProducts.includes(String(item.product_id))
      );
      if (!hasRequiredProduct) {
        return false;
      }
    }

    // Check for excluded products in cart
    if (conditions.excludedProducts && conditions.excludedProducts.length > 0) {
      const hasExcludedProduct = cart.items.some(item =>
        conditions.excludedProducts.includes(String(item.product_id))
      );
      if (hasExcludedProduct) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if product is already in cart
   */
  isProductInCart() {
    const cart = this.getCartData();
    if (!cart) return false;

    return cart.items.some(item => String(item.product_id) === String(this.productId));
  }

  /**
   * Get current cart data from DOM or API
   */
  getCartData() {
    // Try to get cart data from window if available
    if (window.cartData) {
      return window.cartData;
    }

    // Try to parse from cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.cartData) {
      return cartDrawer.cartData;
    }

    // Fallback: check cart items in DOM
    const cartItems = document.querySelectorAll('.cart-item');
    if (cartItems.length === 0) {
      return { items: [], total_price: 0 };
    }

    return null;
  }

  /**
   * Show the upsell component
   */
  show() {
    this.classList.remove('cart-upsell--hidden');
    this.isVisible = true;
    this.setAttribute('aria-hidden', 'false');
  }

  /**
   * Hide the upsell component
   */
  hide() {
    this.classList.add('cart-upsell--hidden');
    this.isVisible = false;
    this.setAttribute('aria-hidden', 'true');
  }

  /**
   * Set loading state
   */
  setLoadingState(loading) {
    this.isLoading = loading;

    if (this.addButton) {
      this.addButton.disabled = loading;
    }

    if (this.loadingSpinner && this.buttonText) {
      if (loading) {
        this.buttonText.style.display = 'none';
        this.loadingSpinner.style.display = 'block';
      } else {
        this.buttonText.style.display = 'block';
        this.loadingSpinner.style.display = 'none';
      }
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    if (!this.errorContainer) return;

    const errorText = this.errorContainer.querySelector('.cart-upsell__error-text');
    if (errorText) {
      errorText.textContent = message;
    }

    this.errorContainer.style.display = 'block';
  }

  /**
   * Hide error message
   */
  hideError() {
    if (this.errorContainer) {
      this.errorContainer.style.display = 'none';
    }
  }

  /**
   * Show success message
   */
  showSuccess() {
    if (this.successContainer) {
      this.successContainer.style.display = 'block';
    }
  }

  /**
   * Hide success message
   */
  hideSuccess() {
    if (this.successContainer) {
      this.successContainer.style.display = 'none';
    }
  }

  /**
   * Public API: Programmatically set product
   */
  setProduct(productId, variantId) {
    this.productId = productId;
    this.variantId = variantId;
    this.dataset.productId = productId;
    this.dataset.variantId = variantId;

    if (this.addButton) {
      this.addButton.dataset.variantId = variantId;
    }

    this.checkVisibilityConditions();
  }

  /**
   * Public API: Programmatically show/hide
   */
  setVisible(visible) {
    if (visible) {
      this.show();
    } else {
      this.hide();
    }
  }
}

// Register the custom element
customElements.define('cart-upsell', CartUpsell);